import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RotateCcw, Save, Sparkles } from 'lucide-react';
import type { DigestDTO } from '@bb/shared';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { relative, utcStamp } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Select, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, ErrorState, Sweep } from '@/components/ui/States';
import { CategoryBadge } from '@/features/feed/CategoryBadge';

function money(costMicros: number | null): string {
  if (costMicros === null) return '—';
  return `$${(costMicros / 1_000_000).toFixed(4)}`;
}

/** The model writes prose with blank-line paragraphs and occasional bullets. */
function SummaryBody({ text }: { text: string }) {
  return (
    <div className="flex flex-col gap-3">
      {text
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block, index) => {
          const isList = /^\s*[-*•]/.test(block);
          if (isList) {
            return (
              <ul key={index} className="flex flex-col gap-1.5 pl-1">
                {block
                  .split('\n')
                  .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
                  .filter(Boolean)
                  .map((line, i) => (
                    <li key={i} className="flex gap-2 text-[14px] leading-relaxed text-ink">
                      <span className="mt-[7px] size-1 shrink-0 rounded-full bg-accent" />
                      <span>{line}</span>
                    </li>
                  ))}
              </ul>
            );
          }
          const isHeading = /^#{1,4}\s/.test(block) || /^\*\*[^*]+\*\*$/.test(block);
          return (
            <p
              key={index}
              className={cn(
                'whitespace-pre-wrap text-[14px] leading-relaxed',
                isHeading
                  ? 'label-xs !text-[11px] !text-accent'
                  : 'text-ink',
              )}
            >
              {block.replace(/^#+\s*/, '').replace(/\*\*/g, '')}
            </p>
          );
        })}
    </div>
  );
}

function PromptEditor() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['digest', 'prompt'],
    queryFn: () => api.digests.getPrompt(),
    staleTime: 5 * 60_000,
  });

  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    if (query.data && draft === null) setDraft(query.data.prompt);
  }, [query.data, draft]);

  const save = useMutation({
    mutationFn: (prompt: string) => api.digests.setPrompt(prompt),
    onSuccess: ({ prompt }) => {
      setDraft(prompt);
      void queryClient.invalidateQueries({ queryKey: ['digest', 'prompt'] });
    },
  });

  const dirty = draft !== null && query.data ? draft !== query.data.prompt : false;

  return (
    <Panel
      label="Instructions"
      className="min-h-0"
      bodyClassName="flex min-h-0 flex-col gap-2"
      actions={
        <>
          {query.data ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDraft(query.data.defaultPrompt)}
              icon={<RotateCcw size={11} />}
            >
              Default
            </Button>
          ) : null}
          <Button
            size="sm"
            variant={dirty ? 'primary' : 'outline'}
            disabled={!dirty || save.isPending}
            onClick={() => draft !== null && save.mutate(draft)}
            icon={<Save size={11} />}
          >
            {save.isPending ? 'Saving' : 'Save'}
          </Button>
        </>
      }
    >
      <p className="font-mono text-[10px] leading-relaxed text-ink-faint">
        Sent as the system prompt before every generation, and cached by DeepSeek — keeping it
        stable is what makes each summary cost a fraction of a cent.
      </p>
      <Textarea
        value={draft ?? ''}
        onChange={(event) => setDraft(event.target.value)}
        rows={18}
        spellCheck={false}
        className="min-h-0 flex-1 resize-none font-mono !text-[11px] leading-relaxed"
        placeholder="Loading…"
      />
      {save.error ? (
        <p className="font-mono text-[11px] text-alert">
          {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
        </p>
      ) : null}
    </Panel>
  );
}

export function DigestPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>('');

  const index = useQuery({
    queryKey: ['digest', 'index'],
    queryFn: () => api.digests.list(),
  });

  const date = selected || index.data?.today || '';

  const digest = useQuery({
    queryKey: ['digest', 'day', date],
    queryFn: async (): Promise<DigestDTO | null> => {
      try {
        return (await api.digests.get(date)).digest;
      } catch (error) {
        // Not generated yet is the normal first state, not a failure.
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: Boolean(date),
  });

  const generate = useMutation({
    mutationFn: ({ force }: { force: boolean }) => api.digests.generate(date, force),
    onSuccess: ({ digest: fresh }) => {
      queryClient.setQueryData(['digest', 'day', date], fresh);
      void queryClient.invalidateQueries({ queryKey: ['digest', 'index'] });
    },
  });

  const enabled = index.data?.enabled ?? true;
  const current = digest.data;

  return (
    <div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
      <Panel
        label="Day summary"
        className="min-h-0"
        bodyClassName="min-h-0 overflow-y-auto"
        actions={
          <>
            <Select
              value={date}
              onChange={(event) => setSelected(event.target.value)}
              className="w-36"
              aria-label="Day"
            >
              {index.data?.today && !index.data.digests.some((d) => d.date === index.data.today) ? (
                <option value={index.data.today}>{index.data.today} (today)</option>
              ) : null}
              {index.data?.digests.map((item) => (
                <option key={item.id} value={item.date}>
                  {item.date}
                  {item.date === index.data?.today ? ' (today)' : ''}
                </option>
              ))}
            </Select>

            <Button
              size="sm"
              variant="primary"
              disabled={!enabled || generate.isPending || !date}
              onClick={() => generate.mutate({ force: Boolean(current) })}
              icon={<Sparkles size={12} />}
            >
              {generate.isPending ? 'Generating' : current ? 'Regenerate' : 'Generate'}
            </Button>
          </>
        }
      >
        {!enabled ? (
          <EmptyState
            title="DeepSeek not configured"
            detail="Set the DEEPSEEK_API_KEY secret and redeploy. Posts keep being archived and classified-free in the meantime."
          />
        ) : generate.isPending ? (
          <div className="flex flex-col gap-3 p-6">
            <p className="label-xs">Reading the day and writing the briefing…</p>
            <Sweep />
          </div>
        ) : generate.error ? (
          <ErrorState
            message={
              generate.error instanceof ApiError
                ? generate.error.message
                : 'Generation failed.'
            }
            action={
              <Button size="sm" onClick={() => generate.mutate({ force: true })}>
                Try again
              </Button>
            }
          />
        ) : digest.isPending ? (
          <div className="p-6">
            <Sweep />
          </div>
        ) : !current ? (
          <EmptyState
            title={`No summary for ${date}`}
            detail="Generate one from the posts archived that day. Emergencies and dramas are fed to the model first, so a busy day never loses its most important items."
          />
        ) : (
          <article className="flex flex-col gap-5 p-4">
            <header className="flex flex-col gap-2 border-b border-line pb-4">
              {current.headline ? (
                <h1 className="text-lg font-medium leading-snug text-ink">{current.headline}</h1>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={current.status === 'READY' ? 'accent' : 'amber'}>
                  {current.status}
                </Badge>
                <span className="data-mono text-[10px] text-ink-faint">{current.date}</span>
                {current.generatedAt ? (
                  <span
                    className="data-mono text-[10px] text-ink-faint"
                    title={utcStamp(current.generatedAt)}
                  >
                    generated {relative(current.generatedAt)} ago
                  </span>
                ) : null}
                {current.model ? (
                  <span className="data-mono text-[10px] text-ink-faint">{current.model}</span>
                ) : null}
                <span className="data-mono text-[10px] text-ink-faint">
                  {money(current.costMicros)}
                </span>
              </div>
            </header>

            {current.summary ? <SummaryBody text={current.summary} /> : null}

            {current.items.length > 0 ? (
              <section className="flex flex-col gap-2 border-t border-line pt-4">
                <span className="label-xs">Sources</span>
                {current.items.map((item) => (
                  <a
                    key={item.post.id}
                    href={item.post.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="group flex items-start gap-2 rounded-sharp border border-transparent px-2 py-1.5 transition-colors hover:border-line hover:bg-panel-alt"
                  >
                    <span className="data-mono mt-0.5 w-5 shrink-0 text-[10px] text-ink-faint">
                      {item.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="data-mono text-[11px] text-ink-dim">
                          @{item.post.author.handle}
                        </span>
                        {item.post.analysis ? (
                          <CategoryBadge analysis={item.post.analysis} />
                        ) : null}
                        {item.post.deletedAt ? <Badge tone="alert">Deleted</Badge> : null}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-ink">
                        {item.post.analysis?.summary ?? item.post.text}
                      </p>
                    </div>
                    <ExternalLink
                      size={12}
                      className="mt-1 shrink-0 text-ink-faint opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </a>
                ))}
              </section>
            ) : null}
          </article>
        )}
      </Panel>

      <PromptEditor />
    </div>
  );
}
